import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  MessageCircle,
  ShieldCheck,
  Zap,
  BarChart3,
  Wallet,
  Package,
  ArrowUpRight,
  Sparkles,
  Lock,
  RefreshCcw,
  Check,
  Link2,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import ConnectInstagramButton from "@/components/ConnectInstagramButton";
import FounderStrip from "@/components/FounderStrip";
import MetaTrustStrip from "@/components/MetaTrustStrip";
import PhoneDemoVideo from "@/components/PhoneDemoVideo";
import DemoVideoPlayer from "@/components/DemoVideoPlayer";
import HeroChatDemo from "@/components/HeroChatDemo";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { faqs } from "@/data/faqs";

const MERCHANT_IMG =
  "https://images.checkoutdm.com/static/merchant-feature.jpg";
const BUYER_IMG =
  "https://images.checkoutdm.com/static/buyer-feature.jpg";
const DEMO_VIDEO_URL =
  "https://images.checkoutdm.com/static/video/checkout-dm-demo.mp4";
const DEMO_VIDEO_POSTER =
  "https://images.checkoutdm.com/static/video/checkoutdm-automation-flow.jpg";
const MERCHANT_VIDEO_URL =
  "https://REPLACE-WITH-YOUR-R2-URL/merchant-dashboard-demo.mp4";

const steps = [
  {
    n: "01",
    title: "Buyer slides into DM",
    body: "A shopper messages your Instagram with what they want — an item, a size, a story reply.",
  },
  {
    n: "02",
    title: "Checkout DM replies",
    body: "Our automation sends a secure checkout link inside the same DM. No app switch, no cart drop-off.",
  },
  {
    n: "03",
    title: "One-tap payment",
    body: "Buyer pays with UPI, card or wallet in seconds. You get a real-time ping.",
  },
  {
    n: "04",
    title: "Order fulfilled",
    body: "Manage orders and returns from your dashboard. With a courier partner connected, the buyer gets the tracking link automatically by email and DM.",
  },
];

const features = [
  {
    icon: MessageCircle,
    title: "DM-native checkout",
    body: "Sell where your audience already is. No landing pages, no friction.",
    span: "md:col-span-2",
    accent: "bg-hotpink text-white",
  },
  {
    icon: ShieldCheck,
    title: "Secure by design",
    body: "PCI-safe payment links, signed URLs, and OAuth-based merchant auth.",
    accent: "bg-mint text-ink",
  },
  {
    icon: Zap,
    title: "Instant automations",
    body: "Story replies, keyword triggers, comment-to-checkout — all set up in minutes.",
    accent: "bg-peach text-ink",
  },
  {
    icon: BarChart3,
    title: "Live analytics",
    body: "Revenue, response time, conversion — track what turns DMs into money.",
    accent: "bg-lilac text-ink",
  },
  {
    icon: Wallet,
    title: "UPI · Cards · Wallets",
    body: "India-first payment stack with international rails for all users.",
    accent: "bg-white text-ink",
  },
  {
    icon: Package,
    title: "Order tracking",
    body: "With a courier partner connected, the buyer gets the tracking link automatically by email and DM, so they never have to ask 'where is my order?'",
    span: "md:col-span-2",
    accent: "bg-ink text-cream",
  },
];

const FEATURE_LINKS = {
  "DM-native checkout": "/features/instagram-dm-automation",
  "Secure by design": "/security",
  "Instant automations": "/features/instagram-comment-to-dm-automation",
  "Order tracking": "/features/instagram-order-management",
};

export default function Home() {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  // Progressive FAQ reveal: start with 6 visible, add 4 more each time the
  // visitor opens a question they haven't opened before — up to the full list.
  // The JSON-LD above still includes ALL faqs regardless of visibleCount, so
  // crawlers/AEO always see the full FAQ content even before any clicks.
  const INITIAL_VISIBLE = 6;
  const REVEAL_STEP = 4;
  const [visibleCount, setVisibleCount] = useState(
    Math.min(INITIAL_VISIBLE, faqs.length)
  );
  const [triggeredIndexes, setTriggeredIndexes] = useState(() => new Set());
  const visibleFaqs = faqs.slice(0, visibleCount);
  const [heroVideoPlaying, setHeroVideoPlaying] = useState(false);

  const handleFaqAccordionChange = (value) => {
    if (!value) return; // an item was closed, not opened
    const idx = Number(value.replace("item-", ""));
    if (!triggeredIndexes.has(idx) && visibleCount < faqs.length) {
      setTriggeredIndexes((prev) => new Set(prev).add(idx));
      setVisibleCount((prev) => Math.min(prev + REVEAL_STEP, faqs.length));
    }
  };

  return (
    <>
      <SEO
        title="Checkout DM | Instagram DM Checkout and Automation for Sellers"
        description="Checkout DM is an Instagram DM checkout and automation tool. Customers browse, pick a size and pay inside the chat. Free plan, no commission, no website needed."
        path="/"
        structuredData={faqSchema}
      />
      <Navbar />

      {/* HERO */}
      <section className="relative isolate overflow-hidden pt-10 md:pt-16">
        {/* drifting colour blobs behind the hero */}
        <div className="blob-drift pointer-events-none absolute -top-24 right-[8%] -z-10 h-80 w-80 rounded-full bg-hotpink/25 blur-3xl" />
        <div className="blob-drift-slow pointer-events-none absolute top-40 -left-16 -z-10 h-72 w-72 rounded-full bg-peach/40 blur-3xl" />
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-6 pb-20 md:grid-cols-12 md:gap-8 md:px-12 md:pb-32">
          <div className="md:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 rounded-full border border-ink/10 bg-white/70 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-ink/70 backdrop-blur"
              data-testid="hero-tag"
            >
              <Sparkles size={14} className="text-hotpink" />
              Instagram DM checkout and automation
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="mt-6 font-display text-5xl leading-[0.95] tracking-tighter md:text-7xl"
              data-testid="hero-title"
            >
              Convert
              <br />
              <span className="text-sunset whitespace-nowrap">Instagram DMs</span>
              <br />
              to Sales Instantly.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="mt-6 max-w-xl text-lg text-ink/70"
            >
              Checkout DM is an Instagram DM checkout and automation tool for
              sellers. Customers browse your products, choose a size, enter
              their address and pay inside the chat, from a comment, a Story
              reply or a DM. No website needed and no commission on your sales.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <ConnectInstagramButton testId="hero-connect-instagram" />
              <Link
                to="/pricing"
                data-testid="hero-see-pricing"
                className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 bg-white/70 px-6 py-4 text-base font-semibold text-ink hover:bg-white"
              >
                See pricing <ArrowUpRight size={18} />
              </Link>
            </motion.div>

            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-ink/60">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-mint" /> Live in 5 mins
              </div>
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-peach" /> UPI + Cards
              </div>
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-hotpink" /> 0% setup fee
              </div>
            </div>

            <a           
              href="https://checkoutdm.com/store/genderrevealgames"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-see-real-store"
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-hotpink hover:underline"
            >
              See a real Checkout DM store <ArrowUpRight size={14} />
            </a>
            <Link
              to="/features"
              data-testid="hero-see-features"
              className="mt-3 flex w-fit items-center gap-1.5 text-sm font-semibold text-ink/70 hover:text-hotpink"
            >
              See everything Checkout DM does <ArrowUpRight size={14} />
            </Link>
          </div>

          <div className="relative md:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7 }}
              className="relative"
            >
              <div className="relative mx-auto w-full max-w-[300px]">
                <PhoneDemoVideo
                  videoUrl={DEMO_VIDEO_URL}
                  posterUrl={DEMO_VIDEO_POSTER}
                  title="Turn every DM into a checkout"
                  onPlayingChange={setHeroVideoPlaying}
                />
                <AnimatePresence>
                  {!heroVideoPlaying && (
                    <>
                      <motion.div
                        key="example-checkout-card"
                        initial={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="glass float-y absolute -left-6 -bottom-6 w-64 rounded-2xl p-4 shadow-xl"
                      >
                        <HeroChatDemo />
                      </motion.div>
                      <motion.div
                        key="commission-card"
                        initial={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="glass float-y-slow absolute -right-6 top-8 w-56 rounded-2xl p-4 shadow-xl"
                      >
                        <p className="text-xs uppercase tracking-[0.2em] text-ink/60">
                          Commission
                        </p>
                        <p className="font-display text-3xl font-black text-ink">0%</p>
                        <p className="text-xs text-ink/60">Every rupee goes to your account</p>
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        </div>

        {/* marquee */}
        <div className="border-y border-ink/10 bg-white/60 py-4 backdrop-blur">
          <div className="marquee-track flex w-max items-center gap-14 whitespace-nowrap px-8 text-sm font-bold uppercase tracking-[0.2em] text-ink/60">
            {Array.from({ length: 2 }).flatMap((_, i) => [
              <span key={`a${i}`}>Instagram DMs</span>,
              <span key={`b${i}`} className="text-hotpink">●</span>,
              <span key={`c${i}`}>UPI Checkout</span>,
              <span key={`d${i}`} className="text-sunset">●</span>,
              <span key={`e${i}`}>Comment to Cart</span>,
              <span key={`f${i}`} className="text-peach">●</span>,
              <span key={`g${i}`}>Story Reply → Sale</span>,
              <span key={`h${i}`} className="text-hotpink">●</span>,
              <span key={`i${i}`}>Automation Playbooks</span>,
              <span key={`j${i}`} className="text-sunset">●</span>,
            ])}
          </div>
        </div>
      </section>

      {/* WHAT IS CHECKOUT DM */}
      <section id="what-is-checkout-dm" className="px-6 py-20 md:px-12 md:py-24">
        <div className="mx-auto max-w-6xl">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">
              What is Checkout DM?
            </p>
            <h2 className="mt-3 font-display text-4xl leading-tight tracking-tighter md:text-5xl">
              A checkout that lives inside your Instagram DMs.
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-ink/70">
              Checkout DM connects to your Instagram professional account through
              Meta’s official login. When a customer messages you, comments a keyword
              or replies to your Story, it shows your catalog, collects their size and
              delivery details and takes payment in the chat. You manage orders,
              returns and shipping from one dashboard.
            </p>
          </div>

          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {[
              {
                icon: MessageCircle,
                title: "Made for Instagram sellers",
                body: "Clothing, jewellery, handmade goods and digital products, sold from the DMs your customers already send.",
              },
              {
                icon: Wallet,
                title: "No website, no commission",
                body: "Payments go straight to your own account. Start on the Free plan and upgrade only when you need more.",
              },
              {
                icon: Link2,
                title: "A Storefront link for anywhere",
                body: "From the Creator plan, share your Storefront on WhatsApp, Telegram, YouTube or a website. Buyers do not need Instagram.",
              },
            ].map((c) => (
              <div key={c.title} className="rounded-3xl border border-ink/10 bg-white p-6">
                <span className="grid h-11 w-11 place-items-center rounded-2xl bg-lilac text-ink">
                  <c.icon size={20} strokeWidth={2.4} />
                </span>
                <h3 className="mt-4 font-display text-xl font-black tracking-tight">{c.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink/70">{c.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm font-semibold">
            <Link to="/features" data-testid="what-is-features-link" className="inline-flex items-center gap-1.5 text-hotpink hover:underline">
              See all features <ArrowUpRight size={14} />
            </Link>
            <Link to="/compare" data-testid="what-is-compare-link" className="inline-flex items-center gap-1.5 text-ink/70 hover:text-hotpink">
              Compare with other tools <ArrowUpRight size={14} />
            </Link>
            <Link to="/pricing" className="inline-flex items-center gap-1.5 text-ink/70 hover:text-hotpink">
              See pricing <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section
        id="how"
        className="relative isolate overflow-hidden bg-ink px-6 py-24 text-cream md:px-12 md:py-32"
      >
        <div className="blob-drift pointer-events-none absolute -top-32 left-[25%] -z-10 h-96 w-96 rounded-full bg-hotpink/25 blur-3xl" />
        <div className="blob-drift-slow pointer-events-none absolute -bottom-32 right-[10%] -z-10 h-96 w-96 rounded-full bg-sunset/20 blur-3xl" />
        <div className="mx-auto max-w-6xl">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">How it works</p>
            <h2 className="mt-3 font-display text-4xl leading-tight tracking-tighter md:text-6xl">
              Four steps between a DM and a <span className="text-shimmer">done deal.</span>
            </h2>
          </div>

          <div className="relative mt-14 grid gap-6 md:grid-cols-4">
            {/* connector line that draws itself across the cards on scroll */}
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, ease: "easeInOut" }}
              className="pointer-events-none absolute left-[12%] right-[12%] top-12 hidden h-px origin-left bg-gradient-to-r from-hotpink via-sunset to-peach md:block"
            />
            {steps.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                whileHover={{ y: -6, transition: { duration: 0.2 } }}
                transition={{ delay: i * 0.15, duration: 0.5 }}
                className="relative rounded-3xl border border-white/10 bg-[#1C1917] p-6 transition-colors hover:border-hotpink/50"
                data-testid={`how-step-${i + 1}`}
              >
                <p className="font-display text-5xl font-black text-sunset">{s.n}</p>
                <h3 className="mt-4 font-display text-xl font-black tracking-tight">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm text-cream/70">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDER STRIP */}
      <FounderStrip />

      {/* META API TRUST STRIP */}
      <MetaTrustStrip />

      {/* FEATURES BENTO */}
      <section id="features" className="px-6 md:px-12">
        <div className="mx-auto max-w-6xl">
          <div className="flex items-end justify-between gap-6">
            <div className="max-w-2xl">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">
                Features
              </p>
              <h2 className="mt-3 font-display text-4xl leading-tight tracking-tighter md:text-6xl">
                Built for creators who <span className="text-sunset">sell in DMs</span>.
              </h2>
            </div>
            <Link
              to="/features"
              data-testid="features-see-all"
              className="hidden flex-none items-center gap-1.5 text-sm font-bold text-hotpink hover:underline md:inline-flex"
            >
              See all features <ArrowUpRight size={14} />
            </Link>
          </div>

          <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-4">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                whileHover={{ y: -6, transition: { duration: 0.2 } }}
                transition={{ delay: i * 0.05 }}
                className={`group rounded-3xl border border-ink/10 p-7 shadow-sm transition-shadow hover:shadow-xl ${f.accent} ${f.span || ""}`}
                data-testid={`feature-card-${i + 1}`}
              >
                <div className="flex items-center gap-3">
                  <span className="grid h-11 w-11 place-items-center rounded-2xl bg-white/70 text-ink shadow-sm">
                    <f.icon size={20} strokeWidth={2.4} />
                  </span>
                  <h3 className="font-display text-xl font-black tracking-tight">
                    {f.title}
                  </h3>
                </div>
                <p className="mt-4 text-sm opacity-80">{f.body}</p>
                {FEATURE_LINKS[f.title] && (
                  <Link
                    to={FEATURE_LINKS[f.title]}
                    className="mt-4 inline-flex items-center gap-1 text-sm font-bold underline-offset-4 hover:underline"
                  >
                    Learn more <ArrowUpRight size={14} />
                  </Link>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* MERCHANT + BUYER SPLIT */}
      <section className="px-6 py-24 md:px-12 md:py-32">
        <div className="mx-auto grid max-w-6xl gap-6 md:grid-cols-2">
          <div id="merchants" className="relative overflow-hidden scroll-mt-28 rounded-[2rem] border border-ink/10 bg-white p-8 md:p-10">
            <DemoVideoPlayer
              videoUrl={MERCHANT_VIDEO_URL}
              posterUrl={MERCHANT_IMG}
              title="Merchant dashboard walkthrough"
              className="aspect-video w-full rounded-2xl"
            />
            <p className="mt-6 text-xs font-bold uppercase tracking-[0.2em] text-hotpink">
              For merchants
            </p>
            <h3 className="mt-2 font-display text-3xl leading-tight tracking-tighter md:text-4xl">
              Your Instagram profile is now a full checkout suite.
            </h3>
                        <p className="mt-3 text-ink/70">
              Automate replies, send checkout links, track every rupee — from a
              single dashboard.
            </p>
            <ul className="mt-5 space-y-2 text-sm text-ink/70">
              <li className="flex items-start gap-2">
                <Check size={16} className="mt-0.5 flex-none text-hotpink" />
                Free to start — paid plans from ₹499/mo as you grow
              </li>
              <li className="flex items-start gap-2">
                <Check size={16} className="mt-0.5 flex-none text-hotpink" />
                0% commission, ever — payments go straight to your own account
              </li>
              <li className="flex items-start gap-2">
                <Check size={16} className="mt-0.5 flex-none text-hotpink" />
                Live in 5 minutes — no website or developer needed
              </li>
            </ul>
            <a
              href="https://checkoutdm.com/store/genderrevealgames"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-hotpink hover:underline"
            >
              See a real Checkout DM store <ArrowUpRight size={14} />
            </a>
          </div>

          <div id="buyers" className="relative overflow-hidden scroll-mt-28 rounded-[2rem] border border-ink/10 bg-ink p-8 text-cream md:p-10">
            <img
              src={BUYER_IMG}
              alt="Buyer shopping on Instagram"
              className="h-64 w-full rounded-2xl object-cover"
            />
            <p className="mt-6 text-xs font-bold uppercase tracking-[0.2em] text-peach">
              For buyers
            </p>
            <h3 className="mt-2 font-display text-3xl leading-tight tracking-tighter md:text-4xl">
              Tap, pay, done. No new app, no new account.
            </h3>
            <p className="mt-3 text-cream/70">
              Shoppers can stay inside Instagram from the first message to
              payment — no app switch, no new tab, no cart to abandon halfway through.
            </p>
            <ul className="mt-5 space-y-2 text-sm text-cream/70">
              <li className="flex items-start gap-2">
                <Check size={16} className="mt-0.5 flex-none text-peach" />
                Nothing to install, no CheckoutDM account to create
              </li>
              <li className="flex items-start gap-2">
                <Check size={16} className="mt-0.5 flex-none text-peach" />
                Pay the merchant directly via UPI, card or wallet — CheckoutDM never charges you
              </li>
              <li className="flex items-start gap-2">
                <Check size={16} className="mt-0.5 flex-none text-peach" />
                With a courier partner connected, the tracking link arrives automatically by email and, for physical products, in the DM
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* PRICING PREVIEW */}
      <section className="px-6 md:px-12">
        <div className="mx-auto max-w-6xl rounded-[2rem] border border-ink/10 bg-white p-8 md:p-14">
          <div className="grid gap-8 md:grid-cols-2">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Pricing</p>
              <h2 className="mt-3 font-display text-4xl leading-tight tracking-tighter md:text-5xl">
                Start free. Grow into a Pro.
              </h2>
              <p className="mt-4 max-w-md text-ink/70">
                Five plans, priced in both USD and INR. Cancel anytime.
              </p>
              <Link
                to="/pricing"
                data-testid="pricing-preview-cta"
                className="mt-8 inline-flex items-center gap-1.5 rounded-full bg-ink px-6 py-3 text-sm font-semibold text-cream hover:bg-ink/85"
              >
                View full pricing <ArrowUpRight size={16} />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { name: "Free", price: "₹0", tag: "Try it" },
                { name: "Creator", price: "₹499", tag: "For digital creators" },
                { name: "Starter", price: "₹1,499", tag: "For sellers" },
                { name: "Growth", price: "₹2,899", tag: "Most loved", featured: true },
                { name: "Pro", price: "₹6,499", tag: "For brands" },
              ].map((p, i) => (
                <motion.div
                  key={p.name}
                  initial={{ opacity: 0, y: 28, scale: 0.96 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, delay: i * 0.09, ease: [0.2, 0.8, 0.2, 1] }}
                  whileHover={{
                    y: -6,
                    scale: 1.02,
                    transition: { type: "spring", stiffness: 300, damping: 20, delay: 0 },
                  }}
                  className={`rounded-2xl border p-5 ${p.name === "Pro" ? "col-span-2" : ""} ${
                    p.featured
                      ? "plan-glow border-hotpink/30 bg-sunset-gradient text-white"
                      : "border-ink/10 bg-cream"
                  }`}
                  data-testid={`pricing-preview-${p.name.toLowerCase()}`}
                >
                  <p className="text-xs font-bold uppercase tracking-[0.2em] opacity-80">
                    {p.tag}
                  </p>
                  <p className="mt-2 font-display text-2xl font-black">{p.name}</p>
                  <p className="mt-4 font-display text-3xl font-black">{p.price}</p>
                  <p className="text-xs opacity-80">/ month</p>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="mt-10 flex flex-col items-start gap-3 border-t border-ink/10 pt-6 text-xs font-semibold text-ink/60 md:flex-row md:flex-wrap md:items-center md:justify-start md:gap-x-8 md:gap-y-3">
            <span className="flex items-start gap-1.5 md:items-center">
              <Lock size={14} className="mt-0.5 flex-none text-ink/40 md:mt-0" />
              Payments secured by Razorpay · PCI-DSS compliant
            </span>
            <span className="flex items-start gap-1.5 md:items-center">
              <Wallet size={14} className="mt-0.5 flex-none text-ink/40 md:mt-0" />
              UPI · Cards · Netbanking · Wallets
            </span>
            <span className="flex items-start gap-1.5 md:items-center">
              <RefreshCcw size={14} className="mt-0.5 flex-none text-ink/40 md:mt-0" />
              No card required to start · Cancel anytime
            </span>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="px-6 py-24 md:px-12 md:py-32">
        <div className="mx-auto grid max-w-6xl gap-14 md:grid-cols-3">
          <div className="md:col-span-1">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">FAQ</p>
            <h2 className="mt-3 font-display text-4xl leading-tight tracking-tighter md:text-5xl">
              Everything you were about to DM us.
            </h2>
          </div>
          <div className="md:col-span-2">
            <Accordion
              type="single"
              collapsible
              className="w-full"
              data-testid="faq-accordion"
              onValueChange={handleFaqAccordionChange}
            >
              {visibleFaqs.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  data-testid={`faq-item-${i + 1}`}
                  className="border-b border-ink/10"
                >
                  <AccordionTrigger className="text-left font-display text-lg font-black tracking-tight hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-base text-ink/70">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
              {visibleCount < faqs.length && (
                <p className="text-sm text-ink/50">
                  Open a question above to see more.
                </p>
              )}
              <Link
                to="/faq"
                data-testid="faq-view-all-link"
                className="ml-auto inline-flex items-center gap-1 text-sm font-bold text-hotpink hover:underline"
              >
                View all {faqs.length} FAQs →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-6 pb-24 md:px-12">
        <div className="mx-auto max-w-6xl overflow-hidden rounded-[2rem] bg-sunset-gradient p-10 text-white shadow-2xl md:p-16">
          <div className="grid items-center gap-8 md:grid-cols-2">
            <div>
              <h2 className="font-display text-4xl leading-[0.95] tracking-tighter md:text-6xl">
                Your next sale is <br /> already in your DMs.
              </h2>
              <p className="mt-5 max-w-md text-white/85">
                Connect your Instagram and get your first checkout link live in
                under five minutes.
              </p>
            </div>
            <div className="flex md:justify-end">
              <ConnectInstagramButton
                testId="final-cta-connect"
                className="bg-white !text-ink hover:!text-ink"
                label="Connect Instagram"
              />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
